import { observer } from 'mobx-react';
import styled from 'styled-components';

// Components
import { Button } from 'top-component-library';

// Constants
import breakpoints from 'constants/breakpoints';
import { button } from 'constants/style';

// Hooks
import { useStores } from 'hooks/use-stores';
import { useMemo } from 'react';

// Utils
import { Spacing } from '@doordash/design-language';
import CartItem from 'models/CartItem';
import CartModifier from 'models/CartModifier';
import SharedCartItem from 'models/SharedCartItem';
import { formatCentsToPrice } from 'utils/Currency';

enum ButtonState {
  Unavailable = 'Unavailable',
  Customize = 'Customize',
  ReadyForReview = 'ReadyForReview',
  Ready = 'Ready',
}

function SubmitButton({
  handleClick,
  cartItem,
  currItem,
  isNew,
}: {
  handleClick: () => void;
  cartItem: CartItem | SharedCartItem;
  currItem: CartItem | CartModifier;
  isNew: boolean;
}) {
  const { locationStore } = useStores();
  const { currencyCode } = locationStore;

  const priceInCents = cartItem.getTotal('price_in_cents');

  const buttonState = useMemo(() => {
    if (!cartItem.menuItem.isFulfillable) {
      return ButtonState.Unavailable;
    }

    const additionalSelectionRequired = Object.values(currItem?.mods ?? {}).some((group) =>
      group.some((mod) => mod.selected && mod.getErrors().length)
    );

    if (additionalSelectionRequired && currItem.getErrors().length) {
      return ButtonState.Customize;
    }

    if (currItem.parent) {
      return ButtonState.ReadyForReview;
    }

    return ButtonState.Ready;
  }, [currItem.mods, cartItem.menuItem.isFulfillable, currItem, currItem.parent]);

  const buttonText = (() => {
    switch (buttonState) {
      case ButtonState.Unavailable:
        return 'Unavailable';
      case ButtonState.Customize:
        return 'Customize Item';
      case ButtonState.ReadyForReview:
        return 'Save and Review';
      default:
        return isNew ? 'Add to Order' : 'Apply Changes';
    }
  })();

  const showButtonPrice = !!priceInCents && buttonState === ButtonState.Ready;

  return (
    <Container>
      <SubmitButtonButton
        block
        onClick={handleClick}
        disabled={!cartItem?.menuItem?.isFulfillable}
        showprice={showButtonPrice ? 1 : 0}
        size="large"
        type="primary"
        data-testid="add-to-order-button"
      >
        <span>{buttonText}</span>
        {showButtonPrice && <span>{formatCentsToPrice(priceInCents, currencyCode)}</span>}
      </SubmitButtonButton>
    </Container>
  );
}

const SubmitButtonButton = styled(Button)<{ showprice: number }>`
  align-items: center;
  background: var(--color-button-primary__regular);
  box-shadow: ${Spacing.None}px ${Spacing.None}px 6px rgba(0, 0, 0, 0.15);
  border-color: var(--color-button-primary__regular);
  border-radius: var(--button__border-radius);
  display: flex;
  height: ${button.regular}px;
  justify-content: ${({ showprice }) => (showprice ? 'space-between' : 'center')};
  padding: 0 1.5rem;

  span {
    color: #fff;
    display: inline-block;

    // Price
    &:nth-of-type(2) {
      flex-grow: 1;
      text-align: right;
    }
  }

  &:disabled,
  &:disabled:focus,
  &:disabled:hover {
    background-color: var(--color-neutral__line);
    border-color: var(--color-neutral__line);
  }

  &:focus,
  &:hover {
    background-color: var(--color-button-primary__regular);
    border-color: var(--color-button-primary__regular);
  }
`;

const Container = styled.div`
  background: var(--color-neutral__modal-background);
  bottom: 0;
  padding: 0.5rem 1rem 1rem;
  position: fixed;
  width: 100%;

  @media (min-width: ${breakpoints.md}px) {
    position: relative;
    padding: 0.5rem 1.5rem 1.5rem;
  }

  // Transparency effect above the button.
  &::after {
    background: linear-gradient(to bottom, rgba(255, 255, 255, 0) 0%, white 100%);
    box-shadow: none !important;
    content: '';
    height: ${button.regular / 2}px;
    left: 0;
    pointer-events: none;
    position: absolute;
    top: -${button.regular / 2}px;
    width: 100%;

    @media (min-width: ${breakpoints.md}px) {
      width: calc(100% - 1.5rem); // 1.5rem is the width of the 'CustomScroll' bar.
    }
  }
`;

export default observer(SubmitButton);
